import { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Autocomplete,
    TextField,
    Button,
    Stack,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import { searchIcd11 } from '../../../../api/emrMaster';
import SectionHeading from './SectionHeading';

const NAVY = '#052F5D';

// Marks the synthetic "add what I typed" row injected into the dropdown.
// A real ICD-11 result can never collide with it: results are keyed by code,
// and this carries a symbol-like sentinel instead.
const FREE_TEXT_OPTION = '__free_text__';

export default function DiagnosisTab({ encounter, onPatch, disabled }) {
    const [query, setQuery] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pick, setPick] = useState(null);

    useEffect(() => {
        if (!query || query.trim().length < 2) { setOptions([]); return undefined; }
        let cancelled = false;
        const t = setTimeout(() => {
            setLoading(true);
            searchIcd11(query.trim())
                .then((r) => { if (!cancelled) setOptions(r.data || []); })
                .catch(() => { if (!cancelled) setOptions([]); })
                .finally(() => { if (!cancelled) setLoading(false); });
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [query]);

    const items = encounter?.diagnoses || [];

    const add = (markPrimary) => {
        if (!pick) return;
        const next = [...items];
        if (markPrimary) next.forEach((d) => { d.primary = false; });
        next.push({
            code: pick.code,
            description: pick.longDesc || pick.shortDesc || pick.code,
            primary: Boolean(markPrimary),
        });
        onPatch({ diagnoses: next });
        setPick(null);
        setQuery('');
    };

    // Free-text fallback: record a diagnosis that isn't in the ICD-11
    // catalog. Stored with no code; the typed text is the label.
    const addFreeText = (markPrimary = false) => {
        const text = query.trim();
        if (!text) return;
        const next = [...items];
        if (markPrimary) next.forEach((d) => { d.primary = false; });
        next.push({ code: '', description: text, primary: Boolean(markPrimary) });
        onPatch({ diagnoses: next });
        setPick(null);
        setQuery('');
    };

    // The dropdown always ends with an "add what I typed" row.
    //
    // This used to be a button below the search box, where the Autocomplete's
    // own popup covered it — so at the exact moment you needed it (typing
    // something ICD-11 doesn't have, staring at "No matches") it was hidden
    // behind that popup, and the feature read as missing. Putting it in the
    // list means it appears where the user is already looking, and Enter
    // selects it like any other option.
    const shownOptions = query.trim()
        ? [...options, { code: FREE_TEXT_OPTION, longDesc: query.trim() }]
        : options;

    const isFreeTextOption = (o) => o?.code === FREE_TEXT_OPTION;

    // Selecting the synthetic row records it immediately — there is nothing
    // further to choose, and making the user then press "Add" would be a
    // second step for no gain.
    const handlePick = (value) => {
        if (isFreeTextOption(value)) {
            addFreeText(false);
            return;
        }
        setPick(value);
    };

    const removeAt = (idx) => onPatch({ diagnoses: items.filter((_, i) => i !== idx) });
    const togglePrimary = (idx) => {
        const next = items.map((d, i) => ({ ...d, primary: i === idx ? !d.primary : false }));
        onPatch({ diagnoses: next });
    };

    return (
        <Box>
            <SectionHeading
                title="Diagnoses"
                hint="Search ICD-11 by code or by part of the description (e.g. “diabetes”). Not in ICD-11 — like “Medical check-up” for a routine visit? Just type it and pick “Add … as free text” at the bottom of the list."
            />

            {!disabled && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 1.5, borderColor: 'rgba(5,47,93,0.10)' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                        <Autocomplete
                            sx={{ flex: 1, minWidth: 240 }}
                            size="small"
                            options={shownOptions}
                            loading={loading}
                            loadingText="Searching ICD-11…"
                            noOptionsText={
                                query.trim().length < 2
                                    ? 'Type 2 or more characters to search'
                                    : loading ? 'Searching…' : 'No matches'
                            }
                            value={pick}
                            onChange={(_, v) => handlePick(v)}
                            inputValue={query}
                            onInputChange={(_, v) => setQuery(v)}
                            filterOptions={(x) => x}
                            getOptionLabel={(o) => {
                                if (!o) return '';
                                if (isFreeTextOption(o)) return o.longDesc || '';
                                return `${o.code} — ${o.longDesc || o.shortDesc || ''}`;
                            }}
                            isOptionEqualToValue={(a, b) => a?.code === b?.code}
                            renderOption={(props, opt) => (
                                <Box component="li" {...props} key={opt.code}>
                                    {isFreeTextOption(opt) ? (
                                        <Box sx={{ display: 'flex', gap: 1, width: '100%', minWidth: 0, alignItems: 'center' }}>
                                            <AddRoundedIcon sx={{ fontSize: 17, color: NAVY, flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: 13.5, color: 'text.primary', minWidth: 0 }}>
                                                Add “<Box component="span" sx={{ fontWeight: 600 }}>{opt.longDesc}</Box>” as free text
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto', flexShrink: 0 }}>
                                                no code
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', gap: 1.5, width: '100%', minWidth: 0, alignItems: 'baseline' }}>
                                            <Typography
                                                sx={{
                                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                    fontSize: 12.5,
                                                    color: NAVY,
                                                    fontWeight: 600,
                                                    flexShrink: 0,
                                                    minWidth: 70,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {opt.code}
                                            </Typography>
                                            <Typography sx={{ fontSize: 13.5, color: 'text.primary', minWidth: 0 }}>
                                                {opt.longDesc || opt.shortDesc}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}
                            renderInput={(p) => (
                                <TextField
                                    {...p}
                                    label="Search diagnosis"
                                    placeholder="e.g. diabetes, asthma, or a code"
                                    InputProps={{
                                        ...p.InputProps,
                                        endAdornment: (
                                            <>
                                                {loading ? <CircularProgress size={14} sx={{ color: NAVY }} /> : null}
                                                {p.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        <Button
                            onClick={() => add(false)}
                            disabled={!pick}
                            variant="outlined"
                            startIcon={<AddRoundedIcon />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderColor: 'rgba(5,47,93,0.25)',
                                color: NAVY,
                                whiteSpace: 'nowrap',
                                '&:hover': { borderColor: NAVY, bgcolor: 'rgba(5,47,93,0.04)' },
                            }}
                        >
                            Add
                        </Button>
                        <Button
                            onClick={() => add(true)}
                            disabled={!pick}
                            variant="contained"
                            startIcon={<StarRoundedIcon />}
                            sx={{
                                bgcolor: NAVY,
                                boxShadow: 'none',
                                textTransform: 'none',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                '&:hover': { bgcolor: '#03244a', boxShadow: 'none' },
                            }}
                        >
                            Add as primary
                        </Button>
                    </Stack>

                    {/* Same action as the last row of the dropdown, kept for
                        when the list is closed (e.g. after pressing Escape).
                        NOTE: the arrow function matters — passing addFreeText
                        directly would hand it the click event, which is truthy
                        and would silently mark the diagnosis primary. */}
                    {query.trim() && !pick && (
                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed rgba(5,47,93,0.12)' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                Not in ICD-11? Record it as written:
                            </Typography>
                            <Button
                                onClick={() => addFreeText(false)}
                                variant="outlined"
                                size="small"
                                startIcon={<AddRoundedIcon />}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    color: NAVY,
                                    borderColor: 'rgba(5,47,93,0.25)',
                                    '&:hover': { borderColor: NAVY, bgcolor: 'rgba(5,47,93,0.04)' },
                                }}
                            >
                                Add “{query.trim()}” as free-text (no ICD-11 code)
                            </Button>
                        </Box>
                    )}
                </Paper>
            )}

            {items.length === 0 ? (
                <Box
                    sx={{
                        py: 3,
                        px: 2,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(5,47,93,0.025)',
                        border: '1px dashed rgba(5,47,93,0.15)',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        No diagnoses yet. Search above and click <strong>Add</strong>.
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={1}>
                    {items.map((d, i) => (
                        <Paper
                            key={i}
                            variant="outlined"
                            sx={{
                                px: 1.5,
                                py: 1,
                                borderRadius: 1.5,
                                borderColor: d.primary ? 'rgba(5,47,93,0.30)' : 'rgba(5,47,93,0.10)',
                                bgcolor: d.primary ? 'rgba(5,47,93,0.04)' : '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            {!disabled && (
                                <Tooltip title={d.primary ? 'Primary' : 'Mark as primary'} arrow>
                                    <IconButton size="small" onClick={() => togglePrimary(i)}>
                                        {d.primary
                                            ? <StarRoundedIcon sx={{ color: '#C46A00', fontSize: 18 }} />
                                            : <StarOutlineRoundedIcon sx={{ color: 'text.disabled', fontSize: 18 }} />}
                                    </IconButton>
                                </Tooltip>
                            )}
                            {disabled && d.primary && (
                                <StarRoundedIcon sx={{ color: '#C46A00', fontSize: 18, ml: 0.5 }} />
                            )}
                            <Chip
                                label={d.code || d.icd10Code || 'No code'}
                                size="small"
                                variant={(d.code || d.icd10Code) ? 'filled' : 'outlined'}
                                sx={(d.code || d.icd10Code) ? {
                                    bgcolor: NAVY,
                                    color: '#fff',
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                    fontWeight: 600,
                                    fontSize: 12,
                                } : {
                                    borderColor: 'rgba(5,47,93,0.25)',
                                    color: 'text.secondary',
                                    fontWeight: 600,
                                    fontSize: 11.5,
                                }}
                            />
                            <Typography sx={{ flex: 1, fontSize: 14, color: 'text.primary' }}>
                                {d.description}
                            </Typography>
                            {!disabled && (
                                <Tooltip title="Remove" arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => removeAt(i)}
                                        sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                                    >
                                        <DeleteOutlineRoundedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Paper>
                    ))}
                </Stack>
            )}
        </Box>
    );
}
